// frontend/packages/business-console/src/components/inbox/ReplyComposer.jsx

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

import { draftReply } from "../../services/inboxService";

// Single-segment SMS cap (GSM-7 single-segment is 160; we use 160 as the
// soft cap because it's what operators expect to see). Multi-segment is
// allowed up to 1600 (server-enforced) but the segment counter helps the
// operator avoid surprise cost.
const SMS_SEGMENT_BYTES = 160;
const SMS_HARD_MAX = 1600;

function smsSegmentCount(text) {
  if (!text) return 0;
  // Twilio rounds up per 160-char chunk. Approximation only -- accurate
  // segment calc requires GSM-7 vs UCS-2 detection per char, which we
  // skip here since the operator just needs a rough cost hint.
  return Math.max(1, Math.ceil(text.length / SMS_SEGMENT_BYTES));
}

/**
 * Reply composer. Sticks to the bottom of the viewport on phones (parent
 * is responsible for the sticky positioning); renders inline on desktop.
 *
 * Channel-aware:
 *   - "email" (default): renders email composer + "Replying via email" caption.
 *   - "sms": renders SMS composer + "Replying via SMS" caption, segment
 *     counter, and 1600-char hard cap. Server enforces the same cap;
 *     this is just a fast-feedback UX guard.
 *
 * `disabledReason` short-circuits the form with an info banner. Used for
 * archived conversations, opt-outs, missing contact info, etc.
 */
export default function ReplyComposer({
  onSend,
  disabledReason,
  customerEmail,
  customerPhone,
  channel = "email",
  conversationId,
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [draftNote, setDraftNote] = useState("");

  // "Draft with AI": ask the backend for a reply grounded in this thread + the
  // business's own voice/rules, and pre-fill the composer. It NEVER sends; the
  // staff member reads, edits, and clicks Send. Does not overwrite text the
  // user has already typed.
  const handleDraft = async () => {
    if (drafting || !conversationId) return;
    setDrafting(true);
    setError("");
    setDraftNote("");
    try {
      const { draft } = await draftReply(conversationId);
      if (draft) {
        setText((prev) => (prev.trim() ? `${prev.trim()}\n\n${draft}` : draft));
      } else {
        setDraftNote(
          "We could not draft this one. Please write a reply, or try again.",
        );
      }
    } catch (err) {
      setDraftNote(
        err?.response?.data?.message ||
          "Could not draft a reply just now. Please try again.",
      );
    } finally {
      setDrafting(false);
    }
  };

  const isSms = channel === "sms";
  const segments = isSms ? smsSegmentCount(text) : 0;
  const overLimit = isSms && text.length > SMS_HARD_MAX;

  const handleSend = async () => {
    const body = text.trim();
    if (!body) return;
    if (overLimit) return;
    setBusy(true);
    setError("");
    try {
      await onSend({ bodyText: body });
      setText("");
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to send reply",
      );
    } finally {
      setBusy(false);
    }
  };

  if (disabledReason) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {disabledReason}
      </Alert>
    );
  }

  return (
    <Box>
      {isSms ? (
        customerPhone && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
          >
            Replying via SMS to <strong>{customerPhone}</strong>
          </Typography>
        )
      ) : (
        customerEmail && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
          >
            Replying via email to <strong>{customerEmail}</strong>
          </Typography>
        )
      )}

      <TextField
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={isSms ? "Type your text reply…" : "Type your reply…"}
        multiline
        minRows={2}
        maxRows={8}
        fullWidth
        disabled={busy}
        sx={{ "& .MuiInputBase-root": { backgroundColor: "background.paper" } }}
        inputProps={isSms ? { maxLength: SMS_HARD_MAX + 50 } : undefined}
        error={overLimit}
      />

      {isSms && (
        <Typography
          variant="caption"
          sx={{
            color: overLimit ? "error.main" : "text.secondary",
            display: "block",
            mt: 0.5,
          }}
        >
          {text.length}/{SMS_HARD_MAX} chars
          {segments > 1 ? ` · ${segments} segments` : ""}
          {overLimit ? ", too long to send" : ""}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {draftNote && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 0.75 }}
        >
          {draftNote}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mt: 1,
        }}
      >
        {conversationId ? (
          <Button
            variant="text"
            onClick={handleDraft}
            disabled={busy || drafting}
            startIcon={
              drafting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeOutlinedIcon />
              )
            }
            sx={{ textTransform: "none", color: "#6D28D9" }}
          >
            {drafting ? "Drafting…" : "Draft with AI"}
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={busy || !text.trim() || overLimit}
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SendOutlinedIcon />
            )
          }
          sx={{ minWidth: 140, py: 1.25, flexShrink: 0 }}
        >
          {busy
            ? "Sending…"
            : isSms
              ? "Send SMS"
              : "Send reply"}
        </Button>
      </Box>
    </Box>
  );
}
